import { UploaderProps } from 'components/Dropzone/utils';
import useQueryParams from 'hooks/useQueryParams';
import { useMemo } from 'react';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { getIsNodeFile, getIsNodeFolder, sortDataRoomNodes } from 'utils';

const DEFAULT_PLACEHOLDER_PROPS = {
  isPlaceholder: true,
  numberOfDownloads: 0,
  numberOfViews: 0,
  viewedDate: '',
  modifiedAt: new Date().toISOString(),
};

export default function useDisplayedItemsWithPlaceholders(
  currentlyDisplayedNodes: DataRoomNode[],
  placeholders: {
    folders: string[];
    files: UploaderProps[];
  },
) {
  const {
    params: { sort },
  } = useQueryParams();

  return useMemo(() => {
    // Create placeholder nodes that mimic DataRoomNode structure
    const folderPlaceholderNodes = placeholders.folders.map((folderName) => ({
      _id: `placeholder-folder-${folderName}`,
      name: folderName,
      sortName: folderName.toLowerCase(),
      isPlaceholderFolder: true,
      ...DEFAULT_PLACEHOLDER_PROPS,
    }));

    const filePlaceholderNodes = placeholders.files.map((file) => ({
      _id: `placeholder-file-${file.name}`,
      name: file.name,
      sortName: file.name.toLowerCase(),
      // Has document property = file
      document: { _id: `doc-${file.name}` },
      ...file,
      ...DEFAULT_PLACEHOLDER_PROPS,
    }));

    // Combine all nodes and placeholders
    const allItems = [
      ...currentlyDisplayedNodes,
      ...folderPlaceholderNodes,
      ...filePlaceholderNodes,
    ];

    // Use the existing sortDataRoomNodes utility
    const sorted = sortDataRoomNodes(allItems as DataRoomNode[], sort);

    // Separate folders and files for rendering
    const folders = sorted.filter(getIsNodeFolder);
    const files = sorted.filter(getIsNodeFile);

    return { folders, files };
  }, [placeholders, currentlyDisplayedNodes, sort]);
}
